import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { compactNumber, relativeTime } from "../format";
import { KeywordsPanel } from "./KeywordsPanel";
import { PapersPanel } from "./PapersPanel";
import { RecipientsPanel } from "./RecipientsPanel";
import { ThemeToggle } from "./ThemeToggle";
import type { Theme } from "./ThemeToggle";
import { StatStrip } from "./StatStrip";
import type { Keyword, Recipient, Status } from "../../shared/types";

const STATUS_REFRESH_MS = 60_000;
const RUN_POLL_MS = 4_000;
const RUN_POLL_TIMEOUT_MS = 4 * 60_000;

export function Dashboard({
  theme,
  onToggleTheme,
  onSignOut,
}: {
  theme: Theme;
  onToggleTheme: () => void;
  onSignOut: () => void;
}) {
  const [status, setStatus] = useState<Status | null>(null);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const loadAll = useCallback(async () => {
    try {
      const [nextStatus, nextKeywords, nextRecipients] = await Promise.all([
        api.status(),
        api.keywords(),
        api.recipients(),
      ]);
      setStatus(nextStatus);
      setKeywords(nextKeywords.keywords);
      setRecipients(nextRecipients.recipients);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not load dashboard");
    }
  }, []);

  const refresh = useCallback(() => {
    void loadAll();
    setRefreshToken((token) => token + 1);
  }, [loadAll]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      api
        .status()
        .then(setStatus)
        .catch(() => {
          /* transient; the next tick retries */
        });
    }, STATUS_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(
    () => () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    },
    [],
  );

  async function pollNow() {
    if (running) return;
    setRunning(true);
    setError(null);

    const startedAt = Date.now();
    try {
      await api.run();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not start a poll");
      setRunning(false);
      return;
    }

    // The run happens past the response, so watch the run log for completion.
    pollRef.current = window.setInterval(() => {
      api
        .status()
        .then((next) => {
          setStatus(next);
          const finished = (next.lastRun?.finishedAt ?? 0) >= startedAt;
          if (finished || Date.now() - startedAt > RUN_POLL_TIMEOUT_MS) {
            if (pollRef.current !== null) window.clearInterval(pollRef.current);
            pollRef.current = null;
            setRunning(false);
            refresh();
          }
        })
        .catch(() => {
          /* keep waiting */
        });
    }, RUN_POLL_MS);
  }

  async function signOut() {
    try {
      await api.logout();
    } finally {
      onSignOut();
    }
  }

  const seeding = status?.harvest.mode === "seeding";
  const runError = status?.lastRun?.error ?? null;

  return (
    <div className="page">
      <div className="shell">
        <div className="head">
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div className="kicker">arXiv Monitor</div>
            <div className="title">Paper Monitor</div>
            <div className="sub">
              {seeding ? (
                <>
                  Seeding the last 30 days, newest first ·{" "}
                  <span className="mono">{compactNumber(status?.counts.papers ?? 0)}</span> stored
                  {status?.harvest.seedCursor ? (
                    <>
                      {" "}
                      · back to <span className="mono">{status.harvest.seedCursor}</span>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  Watching all of arXiv · polled{" "}
                  <span className="mono">{relativeTime(status?.lastRun?.startedAt ?? null)}</span>
                </>
              )}
            </div>
          </div>

          <div className="head-actions">
            <button className="btn" type="button" onClick={() => void pollNow()} disabled={running}>
              {running ? "Polling…" : "Poll now"}
            </button>
            <ThemeToggle theme={theme} onToggle={onToggleTheme} />
            <button className="btn" type="button" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>

        <StatStrip status={status} />

        {error ? <div className="panel error">{error}</div> : null}

        {status && status.counts.abandonedNotifications > 0 ? (
          <div className="panel">
            <div className="section-label" style={{ color: "var(--accent2)" }}>
              {status.counts.abandonedNotifications} notification(s) gave up after repeated failures
            </div>
            <div style={{ fontSize: 12.5, color: "var(--dim)", marginTop: 7 }}>
              These matches are still listed below — they were never marked as sent. Check the recipient
              list for muted addresses and the run error above, then re-add a working recipient.
            </div>
          </div>
        ) : null}

        {runError ? (
          <div className="panel">
            <div className="section-label" style={{ color: "var(--accent2)" }}>
              Last run reported a problem
            </div>
            <div className="mono" style={{ fontSize: 11.5, color: "var(--dim)", marginTop: 7 }}>
              {runError}
            </div>
          </div>
        ) : null}

        <div className="main-grid">
          <PapersPanel keywords={keywords} recipients={recipients} refreshToken={refreshToken} status={status} />
          <div className="side">
            <KeywordsPanel keywords={keywords} onChanged={refresh} />
            <RecipientsPanel
              recipients={recipients}
              senderEmail={status?.senderEmail ?? ""}
              onChanged={refresh}
            />
          </div>
        </div>

        <div className="foot">Polls every 15 minutes · metadata harvested from arxiv.org via OAI-PMH</div>
      </div>
    </div>
  );
}
